import React, { useState, useEffect } from 'react';
import { Upload, Download, ExternalLink, FileImage, FileText, Palette, Crown, Zap, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const CanvaImporter = () => {
  const { user } = useAuth();
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [canvaDesigns, setCanvaDesigns] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [importMethod, setImportMethod] = useState('api'); // 'api', 'url', 'file'
  const [canvaUrl, setCanvaUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  // Check if user has Rookie Pro+ access
  const hasCanvaAccess = ['rookie_pro', 'pro', 'enterprise'].includes(user?.membership_tier);

  // Mock Canva designs (in production, this would come from Canva API)
  const mockCanvaDesigns = [
    {
      id: 'canva_001',
      name: 'Lead Capture Landing Page',
      type: 'Landing Page',
      thumbnail: '/api/placeholder/300/200',
      created: '2024-10-01',
      format: 'Web Page',
      size: '1080x1920'
    },
    {
      id: 'canva_002', 
      name: 'Product Launch Flyer',
      type: 'Marketing Flyer',
      thumbnail: '/api/placeholder/300/200',
      created: '2024-09-28',
      format: 'Print/Digital',
      size: '8.5x11'
    },
    {
      id: 'canva_003',
      name: 'Social Media Template',
      type: 'Social Post',
      thumbnail: '/api/placeholder/300/200',
      created: '2024-09-25',
      format: 'Square',
      size: '1080x1080'
    }
  ];

  useEffect(() => {
    if (hasCanvaAccess) {
      // In production, this would authenticate and fetch user's Canva designs
      setCanvaDesigns(mockCanvaDesigns);
    }
  }, [hasCanvaAccess]);

  const handleCanvaAPIImport = async (designId) => {
    if (!hasCanvaAccess) {
      toast.error('Canva import requires Rookie Pro membership or higher');
      return;
    }

    setImporting(true);
    setImportProgress(0);

    try {
      // Step 1: Authenticate with Canva
      setImportProgress(20);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 2: Export design from Canva
      setImportProgress(40);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Step 3: Convert to BBC Quick List format
      setImportProgress(60);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 4: Process images and assets
      setImportProgress(80);
      await new Promise(resolve => setTimeout(resolve, 1200));

      // Step 5: Save to user's pages
      setImportProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));

      toast.success('🎨 Canva design imported successfully!');
      
      // Reset state
      setTimeout(() => {
        setImporting(false);
        setImportProgress(0);
        setSelectedDesign(null);
      }, 1000);

    } catch (error) {
      toast.error('Failed to import Canva design');
      setImporting(false);
      setImportProgress(0);
    }
  };

  const handleFileUpload = async (file) => {
    if (!hasCanvaAccess) {
      toast.error('File import requires Rookie Pro membership or higher');
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf', 'text/html'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload PNG, JPG, PDF, or HTML files exported from Canva');
      return;
    }

    setImporting(true);
    setImportProgress(0);

    try {
      setImportProgress(25);
      await new Promise(resolve => setTimeout(resolve, 800));

      setImportProgress(50);
      await new Promise(resolve => setTimeout(resolve, 1000));

      setImportProgress(75);
      await new Promise(resolve => setTimeout(resolve, 800));

      setImportProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));

      toast.success('🎨 File imported and converted successfully!');
      
      setTimeout(() => {
        setImporting(false);
        setImportProgress(0);
        setSelectedFile(null);
      }, 1000);

    } catch (error) {
      toast.error('Failed to process uploaded file');
      setImporting(false);
      setImportProgress(0);
    }
  };

  const handleURLImport = async (url) => {
    if (!hasCanvaAccess) {
      toast.error('URL import requires Rookie Pro membership or higher');
      return;
    }

    if (!url.includes('canva.com')) {
      toast.error('Please provide a valid Canva share URL');
      return;
    }

    setImporting(true);
    setImportProgress(0);

    try {
      setImportProgress(30);
      await new Promise(resolve => setTimeout(resolve, 1000));

      setImportProgress(60);
      await new Promise(resolve => setTimeout(resolve, 1500));

      setImportProgress(90);
      await new Promise(resolve => setTimeout(resolve, 1000));

      setImportProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));

      toast.success('🎨 Canva design imported from URL successfully!');
      
      setTimeout(() => {
        setImporting(false);
        setImportProgress(0);
        setCanvaUrl('');
      }, 1000);

    } catch (error) {
      toast.error('Failed to import from Canva URL');
      setImporting(false);
      setImportProgress(0);
    }
  };

  if (!hasCanvaAccess) {
    return (
      <Card className="border-orange-200">
        <CardHeader className="bg-orange-50">
          <CardTitle className="flex items-center gap-2 text-orange-800">
            <Crown className="h-5 w-5" />
            Canva Integration - Premium Feature
          </CardTitle>
          <CardDescription className="text-orange-600">
            Import your Canva designs directly into BBC Quick List
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 text-center">
          <Palette className="h-16 w-16 mx-auto text-orange-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Upgrade to Access Canva Import
          </h3>
          <p className="text-gray-600 mb-4">
            Current tier: <Badge className="bg-gray-100 text-gray-800">{user?.membership_tier || 'free'}</Badge>
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Canva import is available for Rookie Pro, Pro, and Enterprise members. 
            Easily bring your professional Canva designs into your lead capture pages and marketing materials.
          </p>
          <Button className="btn-primary" onClick={() => toast.info('Contact support to upgrade your membership')}>
            <Crown className="h-4 w-4 mr-2" />
            Upgrade Membership
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Palette className="h-8 w-8 text-purple-600" />
            Canva Import Studio
          </h1>
          <p className="text-gray-600">
            Import your Canva designs directly into BBC Quick List - Rookie Pro+ Feature
          </p>
        </div>
        <Badge className="bg-purple-100 text-purple-800 border-purple-300">
          <Crown className="h-3 w-3 mr-1" />
          Premium Feature
        </Badge>
      </div>

      {/* Import Methods */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Method 1: Canva API Integration */}
        <Card className={`cursor-pointer transition-colors ${
          importMethod === 'api' ? 'border-purple-400 bg-purple-50' : 'hover:border-gray-300'
        }`} onClick={() => setImportMethod('api')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <ExternalLink className="h-4 w-4" />
              Canva Connect API
            </CardTitle>
            <CardDescription>
              Direct integration with your Canva account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✅ Access all your designs</li>
              <li>✅ High quality imports</li>
              <li>✅ Preserve text layers</li>
              <li>✅ Real-time sync</li>
            </ul>
          </CardContent>
        </Card>

        {/* Method 2: URL Import */}
        <Card className={`cursor-pointer transition-colors ${
          importMethod === 'url' ? 'border-purple-400 bg-purple-50' : 'hover:border-gray-300'
        }`} onClick={() => setImportMethod('url')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <ExternalLink className="h-4 w-4" />
              Canva Share URL
            </CardTitle>
            <CardDescription>
              Import using Canva share links
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✅ Quick import</li>
              <li>✅ No account linking</li>
              <li>✅ Public designs only</li>
              <li>⚠️ Limited editing</li>
            </ul>
          </CardContent>
        </Card>

        {/* Method 3: File Upload */}
        <Card className={`cursor-pointer transition-colors ${
          importMethod === 'file' ? 'border-purple-400 bg-purple-50' : 'hover:border-gray-300'
        }`} onClick={() => setImportMethod('file')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Upload className="h-4 w-4" />
              File Upload
            </CardTitle>
            <CardDescription>
              Upload exported Canva files
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✅ PNG, JPG, PDF, HTML</li>
              <li>✅ Offline workflow</li>
              <li>✅ Full control</li>
              <li>⚠️ Manual export needed</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Import Interface */}
      <Card className="border-purple-200">
        <CardHeader className="bg-purple-50">
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <Zap className="h-5 w-5" />
            {importMethod === 'api' && 'Connect to Canva Account'}
            {importMethod === 'url' && 'Import from Canva URL'}
            {importMethod === 'file' && 'Upload Canva Export'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {/* API Method */}
          {importMethod === 'api' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <strong>How it works:</strong> Connect your Canva account to access all your designs directly. 
                We'll import them with full fidelity including text layers and images.
              </div>
              
              {canvaDesigns.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {canvaDesigns.map((design) => (
                    <div key={design.id} className="border rounded-lg p-3 hover:border-purple-300 transition-colors">
                      <div className="aspect-video bg-gray-100 rounded mb-3 flex items-center justify-center">
                        <FileImage className="h-8 w-8 text-gray-400" />
                      </div>
                      <h4 className="font-medium text-gray-900 text-sm mb-1">{design.name}</h4>
                      <p className="text-xs text-gray-500 mb-2">{design.type} • {design.size}</p>
                      <Button 
                        size="sm" 
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        onClick={() => handleCanvaAPIImport(design.id)}
                        disabled={importing}
                      >
                        Import Design
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Connect Canva Account
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* URL Method */}
          {importMethod === 'url' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <strong>Instructions:</strong> Copy the share URL from your Canva design and paste it below. 
                Make sure your design is publicly accessible or shared with a link.
              </div>
              <div className="flex space-x-3">
                <input
                  type="url"
                  value={canvaUrl}
                  onChange={(e) => setCanvaUrl(e.target.value)}
                  placeholder="https://www.canva.com/design/..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={importing}
                />
                <Button 
                  onClick={() => handleURLImport(canvaUrl)}
                  disabled={!canvaUrl || importing}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Import
                </Button>
              </div>
            </div>
          )}

          {/* File Method */}
          {importMethod === 'file' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <strong>Supported formats:</strong> PNG, JPG (for images), PDF (for layouts), HTML (for interactive designs). 
                Export your design from Canva and upload it here.
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  accept=".png,.jpg,.jpeg,.pdf,.html"
                  className="hidden"
                  id="canva-file-upload"
                  disabled={importing}
                />
                <label htmlFor="canva-file-upload" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600">Click to upload or drag and drop</p>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG, PDF, HTML up to 10MB</p>
                </label>
              </div>
              {selectedFile && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-gray-500" />
                    <span className="text-sm text-gray-900">{selectedFile.name}</span>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => handleFileUpload(selectedFile)}
                    disabled={importing}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Process File
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Progress */}
      {importing && (
        <Card className="border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">
                    Importing your Canva design...
                  </span>
                  <span className="text-sm text-gray-500">{importProgress}%</span>
                </div>
                <Progress value={importProgress} className="w-full" />
              </div>
            </div>
            <div className="mt-3 text-xs text-gray-500">
              {importProgress < 25 && "Authenticating with Canva..."}
              {importProgress >= 25 && importProgress < 50 && "Downloading design assets..."}
              {importProgress >= 50 && importProgress < 75 && "Converting to BBC Quick List format..."}
              {importProgress >= 75 && importProgress < 95 && "Processing images and text..."}
              {importProgress >= 95 && "Finalizing import..."}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Benefits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            What You Get with Canva Import
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Design Features</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✅ Preserve original design quality</li>
                <li>✅ Maintain text layers for editing</li>
                <li>✅ Import images and graphics</li>
                <li>✅ Responsive design conversion</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">BBC Integration</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✅ Automatic lead capture forms</li>
                <li>✅ BBC Quick List branding options</li>
                <li>✅ SEO optimization</li>
                <li>✅ Mobile-responsive output</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CanvaImporter;